import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Sparkles, Camera, Palette, Sun, Shield, Star, 
  ChevronRight, MapPin, ArrowRight, Instagram, MoreHorizontal, Flame, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { PresignedImage } from '../components/PresignedMedia';
import { timeAsync, timePageLoad, logTiming } from '../utils/performanceLogger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1656709798329-c6ef352a89df?crop=entropy&cs=srgb&fm=jpg&q=85&w=400';
const DEFAULT_HERO_IMAGE = 'https://images.unsplash.com/photo-1613039704499-1975f634d75f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBjYXIlMjBkZXRhaWxpbmclMjBkYXJrJTIwc3R1ZGlvJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzcwODY4MDg4fDA&ixlib=rb-4.1.0&q=85';

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export default function LandingPage() {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [featuredSidequests, setFeaturedSidequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroImages, setHeroImages] = useState([]);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);
  const [heroSocialHandles, setHeroSocialHandles] = useState({ instagram: [], tiktok: [] });
  
  const pageTimer = timePageLoad('LandingPage');
  const heroIntervalRef = useRef(null);

  useEffect(() => {
    fetchData();
    fetchHeroImages();
    return () => {
      if (heroIntervalRef.current) clearInterval(heroIntervalRef.current);
    };
  }, []);
  
  useEffect(() => {
    if (!loading) {
      pageTimer.markReady({ 
        eventsCount: upcomingEvents.length, 
        sidequestsCount: featuredSidequests.length 
      });
    }
  }, [loading]);

  // Auto-rotate hero images every 10 seconds
  useEffect(() => {
    if (heroImages.length > 1) {
      heroIntervalRef.current = setInterval(() => {
        setCurrentHeroIndex(prev => (prev + 1) % heroImages.length);
      }, 10000);
      return () => clearInterval(heroIntervalRef.current);
    }
  }, [heroImages]);

  // Update social handles when hero image changes
  useEffect(() => {
    if (heroImages.length > 0) {
      const current = heroImages[currentHeroIndex];
      if (current) {
        setHeroSocialHandles({
          instagram: current.instagram_handles || [],
          tiktok: current.tiktok_handles || []
        });
      }
    }
  }, [currentHeroIndex, heroImages]);

  const fetchData = async () => {
    try {
      const [eventsRes, sqRes] = await Promise.all([
        axios.get(`${API}/events?upcoming_only=true&limit=6`).catch(() => ({ data: { events: [] } })),
        axios.get(`${API}/sidequests?status=active&limit=6`).catch(() => ({ data: { sidequests: [] } })),
      ]);
      
      setUpcomingEvents(eventsRes.data.events || []);
      setFeaturedSidequests(sqRes.data.sidequests || sqRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHeroImages = async () => {
    try {
      const response = await axios.get(`${API}/hero-images`);
      const images = response.data.images || response.data || [];
      
      if (images.length > 0) {
        // Pre-resolve presigned URLs
        const resolved = [];
        for (const img of images) {
          if (img.presigned_url) {
            resolved.push({ ...img, url: img.presigned_url });
          } else if (img.file_path) {
            try {
              const presRes = await axios.get(`${API}/r2/${img.file_path.replace('/api/r2/', '')}`);
              resolved.push({ ...img, url: presRes.data.url || DEFAULT_HERO_IMAGE });
            } catch {
              resolved.push({ ...img, url: DEFAULT_HERO_IMAGE });
            }
          }
        }
        if (resolved.length > 0) {
          setHeroImages(resolved);
          setHeroSocialHandles({
            instagram: resolved[0].instagram_handles || [],
            tiktok: resolved[0].tiktok_handles || []
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch hero images:', error);
    }
    setHeroImageLoaded(true);
  };

  const currentHeroUrl = heroImages.length > 0 
    ? heroImages[currentHeroIndex]?.url || DEFAULT_HERO_IMAGE 
    : DEFAULT_HERO_IMAGE;

  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Images with crossfade */}
        {heroImages.length > 0 ? (
          heroImages.map((img, idx) => (
            <div 
              key={idx}
              className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ${
                idx === currentHeroIndex ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ backgroundImage: `url(${img.url})` }}
            />
          ))
        ) : (
          <div 
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ${
              heroImageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ backgroundImage: `url(${DEFAULT_HERO_IMAGE})` }}
          />
        )}
        <div className="absolute inset-0 bg-neutral-900" style={{ zIndex: -1 }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-[#0A0A0A]" />
        
        {/* Hero image indicators */}
        {heroImages.length > 1 && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {heroImages.map((_, idx) => (
              <button
                key={idx}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentHeroIndex ? 'bg-white w-6' : 'bg-white/40'
                }`}
                onClick={() => setCurrentHeroIndex(idx)}
              />
            ))}
          </div>
        )}
        
        {/* Social Handles */}
        {(heroSocialHandles.instagram.length > 0 || heroSocialHandles.tiktok.length > 0) && (
          <div className="absolute top-24 right-4 sm:right-6 z-20 flex flex-col gap-2 animate-fade-in-up">
            {heroSocialHandles.instagram.map((handle, idx) => (
              <a key={`ig-${idx}`} href={`https://instagram.com/${handle.replace('@', '')}`} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 bg-black/40 backdrop-blur-sm border border-white/10 rounded-full px-3 py-1.5 text-sm text-white/90 hover:bg-black/60 hover:border-pink-500/40 transition-all group">
                <Instagram className="w-4 h-4 text-pink-400 group-hover:text-pink-300" />
                <span className="text-white/80 group-hover:text-white">{handle}</span>
              </a>
            ))}
            {heroSocialHandles.tiktok.map((handle, idx) => (
              <a key={`tt-${idx}`} href={`https://tiktok.com/${handle}`} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 bg-black/40 backdrop-blur-sm border border-white/10 rounded-full px-3 py-1.5 text-sm text-white/90 hover:bg-black/60 hover:border-cyan-500/40 transition-all group">
                <TikTokIcon className="w-4 h-4 text-cyan-400 group-hover:text-cyan-300" />
                <span className="text-white/80 group-hover:text-white">{handle}</span>
              </a>
            ))}
          </div>
        )}
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 w-full">
          <div className="max-w-3xl">
            <Badge className="mb-6 bg-red-500/10 text-red-400 border-red-500/20 px-4 py-1.5 text-sm">
              <Flame className="w-3 h-3 mr-1" />
              RydQuest
            </Badge>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-tight mb-6 animate-fade-in-up">
              Car Community,<br />
              <span className="text-red-500">Reimagined</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-neutral-300 mb-10 max-w-xl animate-fade-in-up stagger-1">
              Join a community of car enthusiasts, take on sidequests, attend events, 
              and discover the best in automotive culture.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-2">
              <Link to="/sidequests">
                <Button 
                  size="lg" 
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-[0_0_30px_rgba(239,68,68,0.3)] hover:shadow-[0_0_40px_rgba(239,68,68,0.4)] transition-all"
                  data-testid="explore-sidequests-btn"
                >
                  Explore Sidequests
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/events">
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="border-white/20 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
                  data-testid="view-events-btn"
                >
                  <Calendar className="mr-2 w-5 h-5" />
                  Upcoming Events
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Events Section */}
      {upcomingEvents.length > 0 && (
        <section className="py-20 px-6 bg-[#0A0A0A]">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="text-red-500 text-sm font-semibold uppercase tracking-wider mb-2">Upcoming</p>
                <h2 className="text-3xl sm:text-4xl font-bold text-white">Events</h2>
              </div>
              <Link to="/events" className="hidden sm:flex items-center gap-2 text-neutral-400 hover:text-white transition-colors">
                View all <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingEvents.map((event) => (
                <div 
                  key={event.id}
                  className="group relative overflow-hidden rounded-2xl bg-[#121212] border border-white/10 hover:border-red-500/50 transition-all duration-300 card-hover"
                >
                  <div className="relative h-48 overflow-hidden">
                    {event.image_url ? (
                      <PresignedImage 
                        src={event.image_url}
                        alt={event.name}
                        className="w-full h-full object-cover transition-all duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-red-500/20 to-purple-500/20 flex items-center justify-center">
                        <Calendar className="w-12 h-12 text-red-400/50" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-semibold text-white mb-2">{event.name}</h3>
                    <div className="flex items-center gap-4 text-sm text-neutral-400 mb-2">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(event.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {event.event_time && ` at ${event.event_time}`}
                      </span>
                    </div>
                    {event.location_address && (
                      <div className="flex items-center gap-1 text-sm text-neutral-500">
                        <MapPin className="w-4 h-4" />
                        {event.location_address}
                      </div>
                    )}
                    {event.social_link && (
                      <a href={event.social_link} target="_blank" rel="noopener noreferrer" 
                        className="mt-3 inline-flex items-center gap-1 text-sm text-red-400 hover:text-red-300 transition-colors">
                        View post <ArrowRight className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Active Sidequests Section */}
      {featuredSidequests.length > 0 && (
        <section className="py-20 px-6 bg-[#0A0A0A]">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="text-red-500 text-sm font-semibold uppercase tracking-wider mb-2">Active</p>
                <h2 className="text-3xl sm:text-4xl font-bold text-white">Sidequests</h2>
              </div>
              <Link to="/sidequests" className="hidden sm:flex items-center gap-2 text-neutral-400 hover:text-white transition-colors">
                View all <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredSidequests.map((sq) => (
                <Link 
                  key={sq.id}
                  to={`/sidequests/${sq.id}`}
                  className="group relative overflow-hidden rounded-2xl bg-[#121212] border border-white/10 hover:border-red-500/50 transition-all duration-300 card-hover"
                >
                  <div className="relative h-48 overflow-hidden">
                    {sq.thumbnail_url ? (
                      <PresignedImage 
                        src={sq.thumbnail_url}
                        alt={sq.title}
                        className="w-full h-full object-cover transition-all duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-red-500/20 to-orange-500/20 flex items-center justify-center">
                        <Flame className="w-12 h-12 text-red-400/50" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent" />
                    {sq.is_featured && (
                      <Badge className="absolute top-3 left-3 bg-red-500 text-white">Featured</Badge>
                    )}
                    <Badge className="absolute top-3 right-3 bg-green-500/20 text-green-400 border-green-500/30">
                      {sq.status?.toUpperCase() || 'ACTIVE'}
                    </Badge>
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-semibold text-white mb-1">{sq.title}</h3>
                    <p className="text-sm text-neutral-400 line-clamp-2 mb-3">{sq.description}</p>
                    <div className="flex items-center gap-4 text-sm text-neutral-500">
                      <span>{sq.submission_count || 0} submissions</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-500 flex items-center justify-center">
                <Flame className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-white">RydQuest</span>
            </div>
            <p className="text-sm text-neutral-500">
              © 2026 RydQuest. Car Community, Reimagined.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
